import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { cva } from 'class-variance-authority';
import { cn } from '@/shared/utils/cn.js';
import { useAuth } from '@/shared/context/AuthContext';
import useUser from '@/shared/hooks/useUser';
import {
  User,
  LogOut,
  ChevronDown,
  CreditCard,
  ReceiptText,
  WalletCards,
} from 'lucide-react';

const avatarPlaceholderVariants = cva(
  'border-primary-20 bg-primary-10 text-primary-80 group-hover:bg-primary-20 flex items-center justify-center rounded-xl border transition-all duration-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:group-hover:bg-gray-600',
  {
    variants: {
      size: { sm: 'h-8 w-8', md: 'h-12 w-12 rounded-2xl' },
    },
    defaultVariants: { size: 'sm' },
  },
);

const menuItemVariants = cva(
  'hover:bg-primary-10/60 group flex w-full cursor-pointer items-center space-x-3 space-x-reverse px-4 py-2.5 text-right transition-all duration-200 dark:hover:bg-gray-700/50',
  {
    variants: {
      variant: {
        default: '',
        danger: 'hover:bg-red-50 dark:hover:bg-red-900/20',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

const menuIconVariants = cva(
  'text-tertiary-50 group-hover:text-primary-80 transition-colors duration-200 dark:text-gray-400 dark:group-hover:text-gray-200',
  {
    variants: {
      variant: {
        default: '',
        danger:
          'text-tertiary-50 group-hover:text-red-500 dark:text-gray-400 dark:group-hover:text-red-400',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

const menuTextVariants = cva(
  'text-tertiary-80 group-hover:text-primary-100 text-sm font-medium transition-colors duration-200 dark:text-gray-300 dark:group-hover:text-white',
  {
    variants: {
      variant: {
        default: '',
        danger:
          'text-tertiary-80 group-hover:text-red-600 dark:text-gray-300 dark:group-hover:text-red-400',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

const skeletonLineVariants = cva('animate-pulse rounded', {
  variants: {
    size: {
      triggerName: 'bg-tertiary-40 h-2.5 w-16 shadow-sm dark:bg-gray-600',
      triggerStatus: 'bg-tertiary-20 h-2 w-10 dark:bg-gray-700',
      dropdownName: 'bg-tertiary-40 h-3 w-20 shadow-sm dark:bg-gray-600',
      dropdownEmail: 'bg-tertiary-20 h-2 w-28 dark:bg-gray-700',
    },
  },
});

export default function ProfileDropdown() {
  const [showProfile, setShowProfile] = useState(false);
  const dropdownRef = useRef(null);

  const { logout } = useAuth();
  // اضافه کردن clearUserCache برای اطمینان مضاعف
  const { userDetail, isUserLoading, clearUserCache } = useUser();
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowProfile(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      setShowProfile(false);
      clearUserCache();
      await logout();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  function maskPhoneNumber(phone) {
    if (!phone || phone.length < 7) return phone;
    const start = phone.slice(0, 4);
    const end = phone.slice(7);
    return `${start}***${end}`;
  }

  const displayName =
    userDetail?.first_name && userDetail?.last_name
      ? `${userDetail.first_name} ${userDetail.last_name}`
      : userDetail?.first_name || 'کاربر مهمان';

  const displayPhone = userDetail?.phone_number
    ? maskPhoneNumber(userDetail.phone_number)
    : '';
  const displayEmail = displayPhone || 'user@intelio.com';

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setShowProfile(!showProfile)}
        className="group focus:ring-primary-50/20 border-tertiary-20 hover:border-primary-40/60 hover:bg-tertiary-10/50 relative flex items-center space-x-2 space-x-reverse rounded-xl border bg-white p-1.5 shadow-sm transition-all duration-300 focus:ring-2 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-500 dark:hover:bg-gray-700/50"
      >
        <div className="relative">
          <div className={avatarPlaceholderVariants({ size: 'sm' })}>
            <User size={16} strokeWidth={2.5} />
          </div>
          <div className="absolute -right-0.5 -bottom-0.5 h-3 w-3 rounded-full border-2 border-white bg-green-500 dark:border-gray-800" />
        </div>

        <div className="hidden min-w-[80px] flex-col justify-center text-right lg:flex">
          {isUserLoading ? (
            <>
              <div
                className={cn(
                  skeletonLineVariants({ size: 'triggerName' }),
                  'mb-1.5',
                )}
              />
              <div
                className={skeletonLineVariants({ size: 'triggerStatus' })}
              />
            </>
          ) : (
            <>
              <span className="text-tertiary-90 group-hover:text-primary-100 text-xs leading-tight font-bold transition-colors duration-200 dark:text-gray-200 dark:group-hover:text-white">
                {displayName}
              </span>
              <span className="text-tertiary-60 mt-0.5 text-[11px] leading-tight dark:text-gray-400">
                آنلاین
              </span>
            </>
          )}
        </div>

        <div
          className={cn(
            'text-tertiary-50 group-hover:text-primary-80 ml-1 transition-transform duration-300 dark:text-gray-400',
            showProfile && 'rotate-180',
          )}
        >
          <ChevronDown size={16} />
        </div>
      </button>

      {showProfile && (
        <div className="animate-in fade-in slide-in-from-top-2 border-tertiary-20 shadow-tertiary-20/40 absolute top-full left-0 z-50 mt-2 w-64 rounded-2xl border bg-white/95 py-2 shadow-xl backdrop-blur-xl duration-200 dark:border-gray-700 dark:bg-gray-800/95 dark:shadow-black/40">
          <div className="border-tertiary-20 border-b px-5 py-4 dark:border-gray-700/60">
            <div className="flex items-center space-x-3 space-x-reverse">
              <div className="relative">
                <div className={avatarPlaceholderVariants({ size: 'md' })}>
                  <User size={22} strokeWidth={2} />
                </div>
                <div className="absolute -right-1 -bottom-1 h-3.5 w-3.5 rounded-full border-2 border-white bg-green-500 dark:border-gray-800" />
              </div>
              <div className="flex-1 overflow-hidden">
                {isUserLoading ? (
                  <>
                    <div
                      className={cn(
                        skeletonLineVariants({ size: 'dropdownName' }),
                        'mb-2',
                      )}
                    />
                    <div
                      className={skeletonLineVariants({
                        size: 'dropdownEmail',
                      })}
                    />
                  </>
                ) : (
                  <>
                    <p className="text-tertiary-100 truncate text-sm font-bold dark:text-gray-100">
                      {displayName}
                    </p>
                    <p
                      className="text-tertiary-60 mt-1 truncate text-xs dark:text-gray-400"
                      dir="ltr"
                    >
                      {displayEmail}
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="py-1.5">
            <Link
              to="/profile/info"
              className={menuItemVariants()}
              onClick={() => setShowProfile(false)}
            >
              <User className={menuIconVariants()} size={18} />
              <span className={menuTextVariants()}>پروفایل کاربری</span>
            </Link>
            <Link
              to="/credit"
              className={menuItemVariants()}
              onClick={() => setShowProfile(false)}
            >
              <WalletCards className={menuIconVariants()} size={18} />
              <span className={menuTextVariants()}>اعتبار و افزایش موجودی</span>
            </Link>
            <Link
              to="/subscription"
              className={menuItemVariants()}
              onClick={() => setShowProfile(false)}
            >
              <CreditCard className={menuIconVariants()} size={18} />
              <span className={menuTextVariants()}>پلن و اشتراک</span>
            </Link>
            <Link
              to="/invoices"
              className={menuItemVariants()}
              onClick={() => setShowProfile(false)}
            >
              <ReceiptText className={menuIconVariants()} size={18} />
              <span className={menuTextVariants()}>فاکتورها</span>
            </Link>
          </div>

          <div className="border-tertiary-20 border-t pt-1.5 dark:border-gray-700/60">
            <button
              onClick={handleLogout}
              className={menuItemVariants({ variant: 'danger' })}
            >
              <LogOut
                className={menuIconVariants({ variant: 'danger' })}
                size={18}
              />
              <span className={menuTextVariants({ variant: 'danger' })}>
                خروج از سیستم
              </span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
